import { PictureAsPdf, TableView } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Container, FormControl, Grid, InputLabel, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AppBar from "../components/AppBar";
import { useSidebarLayout } from "../zustand/useSidebarStore";
import CardAnalytic from "../components/cards/CardAnalytic";
import DateRangePickerComponent from "../components/inputs/DateRangePicker";
import VentasChart from "../components/LineChart";
import NavBar from "../components/NavBar";
import getPackagesByRange, { getVentasByFecha, type Analytics } from "../firebase/firestore/paquetes";
import { fetchStaffUsuarios, getStatsByVendedor, type Usuario, type VendedorStats } from "../firebase/firestore/usuarios";
import { exportExcelStatistics, exportPDFStatistics } from "../utils/exportTable";
import { useAgenciaStore } from "../zustand/useAgenciaStore";
import { useDateRangeStore } from "../zustand/useDateRangeStore";

const moneyFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function formatMoney(value: number | undefined) {
    const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
    return moneyFormatter.format(n);
}

const DEFAULT_ESTADOS_ENTREGADO = ["entregado"];

export default function AnalyticsRoute() {
    const sidebar = useSidebarLayout();
    const { agencia } = useAgenciaStore();
    const estadosEntregado = agencia?.estadosEntregado ?? DEFAULT_ESTADOS_ENTREGADO;
    const { fechaInicio, fechaFin, setFechas } = useDateRangeStore();
    const [analytics, setAnalytics] = useState<Analytics>();
    const [data, setData] = useState<{ fecha: string; total: number }[]>([]);
    const [staff, setStaff] = useState<Usuario[]>([]);
    const [vendedorId, setVendedorId] = useState<string>("");
    const [vendedorStats, setVendedorStats] = useState<VendedorStats>();
    const [vendedorLoading, setVendedorLoading] = useState(false);

    useEffect(() => {

        const fetchData = async () => {
            const ventas = await getVentasByFecha(
                fechaInicio.startOf("day").toDate(),
                fechaFin.endOf("day").toDate(),
                estadosEntregado
            );
            setData(ventas);
        };

        fetchData();
        getPackagesByRange(
            fechaInicio.toDate(),
            fechaFin.toDate(),
            estadosEntregado
        )
            .then(response => setAnalytics(response))
    }, [fechaInicio, fechaFin, estadosEntregado])

    useEffect(() => {
        fetchStaffUsuarios().then(setStaff);
    }, []);

    useEffect(() => {
        if (!vendedorId) {
            setVendedorStats(undefined);
            return;
        }
        setVendedorLoading(true);
        getStatsByVendedor(
            vendedorId,
            fechaInicio.startOf("day").toDate(),
            fechaFin.endOf("day").toDate()
        )
            .then(setVendedorStats)
            .finally(() => setVendedorLoading(false));
    }, [vendedorId, fechaInicio, fechaFin]);


    return (
        <>
            <AppBar />
            <Container maxWidth={false} sx={{ mt: 1, p: 1, ...sidebar }}>
                <NavBar />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        marginTop: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            marginTop: 2,
                        }}
                    >
                        <DateRangePickerComponent
                            start={fechaInicio}
                            end={fechaFin}
                            setDate={setFechas}
                            handleChange={() => null}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2
                            }}
                        >
                            <Button
                                size="small"
                                disabled={!analytics}
                                onClick={() => {
                                    const fecha = dayjs().format("DD/MM/YYYY")
                                    const fileName = `estadisticas-${fecha}.pdf`
                                    exportPDFStatistics(
                                        fileName,
                                        "ventas-chart",
                                        agencia?.nombre ?? "",
                                        analytics
                                    )
                                }}
                            >
                                <Tooltip title="Exportar como archivo PDF">
                                    <PictureAsPdf sx={{ width: '1.2em', height: '1.2em' }} />
                                </Tooltip>
                            </Button>
                            <Button
                                size="small"
                                disabled={!analytics}
                                onClick={() => {
                                    const fecha = dayjs().format("DD/MM/YYYY")
                                    const fileName = `estadisticas-${fecha}.xlsx`
                                    exportExcelStatistics(
                                        fileName,
                                        agencia?.nombre ?? "",
                                        analytics
                                    )
                                }}
                            >
                                <Tooltip title="Exportar como archivo .xlsx">
                                    <TableView sx={{ width: '1.2em', height: '1.2em' }} />
                                </Tooltip>
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ width: "100%" }}>
                        <VentasChart data={data} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Grid container spacing={2}>
                            <CardAnalytic title={"Ingresos"} count={`$${formatMoney(analytics?.sales)}`} />
                            <CardAnalytic title={"Cuentas por cobrar"} count={`$${formatMoney(analytics?.cuentasPorCobrar)}`} />
                            <CardAnalytic title={"Costos"} count={`$${formatMoney(analytics?.costos)}`} />
                            <CardAnalytic title={"Ganancia"} count={`$${formatMoney(analytics?.ganancia)}`} />
                            <CardAnalytic title={"Paquetes aéreos"} count={analytics?.aereo ?? 0} />
                            <CardAnalytic title={"Paquetes marítimos"} count={analytics?.maritimo ?? 0} />
                            <CardAnalytic title={"Total paquetes"} count={analytics?.packages ?? 0} />
                        </Grid>
                    </Box>

                    {/* Estadísticas por vendedor */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            Generado por clientes de un vendedor
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Selecciona un miembro del staff para ver cuántos paquetes y
                            dinero han generado los clientes asignados a él (campo
                            "Vendedor").
                        </Typography>
                        <FormControl sx={{ maxWidth: 320 }}>
                            <InputLabel id="vendedor-stats-label">Vendedor</InputLabel>
                            <Select
                                labelId="vendedor-stats-label"
                                value={vendedorId}
                                label="Vendedor"
                                onChange={(e) => setVendedorId(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="">
                                    <em>Ninguno</em>
                                </MenuItem>
                                {staff.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {`${u.name} ${u.lastName}`}
                                        {u.role ? ` — ${u.role}` : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {vendedorId && (
                            <Grid container spacing={2}>
                                {[
                                    { label: "Clientes asignados", value: vendedorStats?.clientes ?? 0 },
                                    { label: "Total paquetes", value: vendedorStats?.packages ?? 0 },
                                    { label: "$ Generado", value: `$${formatMoney(vendedorStats?.total)}` },
                                    { label: "Paquetes aéreos", value: vendedorStats?.aereo ?? 0 },
                                    { label: "Paquetes marítimos", value: vendedorStats?.maritimo ?? 0 },
                                ].map((stat) => (
                                    <Grid size={{ xs: 12, md: 4 }} key={stat.label}>
                                        <Card sx={{ width: "95%", margin: "0 auto", minHeight: 100 }}>
                                            <CardContent>
                                                <Typography variant="h4" fontWeight={700}>
                                                    {vendedorLoading ? "…" : stat.value}
                                                </Typography>
                                                <Typography fontSize={14} fontWeight={600} color="text.secondary">
                                                    {stat.label}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Box>
            </Container>
        </>
    );
}
